/**
 * Party Mode — Supabase Realtime rooms.
 * DJ creates a room, players join via code, play songs together.
 */

import { supabase } from "./supabase";

export interface Room {
  id: string;
  code: string;
  host_id: string;
  mode: "buzzer" | "turns";
  status: "waiting" | "playing" | "finished";
  current_song_index: number;
}

export interface RoomPlayer {
  id: string;
  user_id: string;
  display_name: string;
  score: number;
  is_host: boolean;
}

/** 5-char room code from an unambiguous alphabet (no 0/O, 1/I/L, 8/B, 5/S,
 *  2/Z confusions). 20^5 = 3.2M codes — fine for party room uniqueness,
 *  and every char is unambiguous whether read from screen, screenshot, or
 *  passed to a copy/paste pipeline. */
function generateRoomCode(): string {
  const alphabet = "ACDEFGHJKMNPQRTUVWXY";
  let out = "";
  for (let i = 0; i < 5; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
  }
  return out;
}

/** Create a new party room */
export async function createRoom(mode: "buzzer" | "turns"): Promise<Room | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const code = generateRoomCode();
  const { data, error } = await supabase
    .from("rooms")
    .insert({
      host_id: user.id,
      mode,
      content_mode: "endless",
      code,
      status: "waiting",
    })
    .select()
    .maybeSingle();

  if (error || !data) {
    console.error("Failed to create room:", error?.message);
    return null;
  }

  // Add host as player
  const { data: profile } = await supabase
    .from("profiles")
    .select("display_name")
    .eq("id", user.id)
    .maybeSingle();

  await supabase.from("room_players").insert({
    room_id: data.id,
    user_id: user.id,
    display_name: profile?.display_name ?? "DJ",
    is_host: true,
  });

  return data as Room;
}

/** Join (or resume) a room by code.
 *
 *  Idempotent: a refresh, host tapping their own share link, or a player
 *  re-joining after network drop all return the current room state without
 *  duplicate-insert errors. Uses upsert on the (room_id, user_id) unique
 *  constraint to let the re-joiner land back in their existing row.
 *
 *  Accepts rooms in "waiting" OR "playing" status — mid-game re-joins are
 *  useful when a player's connection drops. Only "finished" rooms are
 *  rejected.
 */
export async function joinRoom(code: string): Promise<{ room: Room; players: RoomPlayer[] } | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data: room, error } = await supabase
    .from("rooms")
    .select("*")
    .eq("code", code.toUpperCase())
    .in("status", ["waiting", "playing"])
    .maybeSingle();

  if (error || !room) return null;

  const isHost = room.host_id === user.id;

  const { data: profile } = await supabase
    .from("profiles")
    .select("display_name")
    .eq("id", user.id)
    .maybeSingle();

  // Upsert — first join inserts, re-join/host-self-join hits the unique
  // constraint on (room_id, user_id) and is ignored cleanly.
  await supabase.from("room_players").upsert(
    {
      room_id: room.id,
      user_id: user.id,
      display_name: profile?.display_name ?? (isHost ? "DJ" : "Player"),
      is_host: isHost,
    },
    { onConflict: "room_id,user_id", ignoreDuplicates: true }
  );

  // Get all players
  const { data: players } = await supabase
    .from("room_players")
    .select("*")
    .eq("room_id", room.id);

  return { room: room as Room, players: (players ?? []) as RoomPlayer[] };
}

/** Get current players in a room */
export async function getRoomPlayers(roomId: string): Promise<RoomPlayer[]> {
  const { data } = await supabase
    .from("room_players")
    .select("*")
    .eq("room_id", roomId)
    .order("score", { ascending: false });

  return (data ?? []) as RoomPlayer[];
}

/** Subscribe to room player changes (realtime) */
export function subscribeToRoom(
  roomId: string,
  onPlayersUpdate: (players: RoomPlayer[]) => void,
  onStatusChange?: (status: Room["status"]) => void,
) {
  const channel = supabase
    .channel(`room:${roomId}`)
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "room_players", filter: `room_id=eq.${roomId}` },
      () => {
        getRoomPlayers(roomId).then(onPlayersUpdate);
      }
    )
    .on(
      "postgres_changes",
      { event: "UPDATE", schema: "public", table: "rooms", filter: `id=eq.${roomId}` },
      (payload) => {
        const newStatus = (payload.new as any).status as Room["status"];
        onStatusChange?.(newStatus);
      }
    )
    .subscribe();

  return () => { supabase.removeChannel(channel); };
}

/** Update a player's score.
 *
 *  Before migration 014 this silently failed due to a missing UPDATE policy
 *  on room_players; the error is now logged so any future RLS/constraint
 *  regression surfaces instead of eating scores. */
export async function updatePlayerScore(roomId: string, points: number): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;

  const { data: current } = await supabase
    .from("room_players")
    .select("score")
    .eq("room_id", roomId)
    .eq("user_id", user.id)
    .maybeSingle();

  const { error } = await supabase
    .from("room_players")
    .update({ score: (current?.score ?? 0) + points })
    .eq("room_id", roomId)
    .eq("user_id", user.id);

  if (error) console.error("updatePlayerScore failed:", error.message);
}

/** Update room status */
export async function updateRoomStatus(roomId: string, status: "waiting" | "playing" | "finished"): Promise<void> {
  await supabase.from("rooms").update({ status }).eq("id", roomId);
}
